import { useCallback } from 'react';

import { LOCAL_STORAGE } from '@/constants';
import { useDataModel } from '@/hooks/useDataModel';
import { DataModelAttribute, DataModelAttributeBaseClass, DataModelAttributeTypeName } from '@/types/DataModel';

import { useOptionTranslator } from '../hooks/useOptionTranslator';

/**
 * Read-only, single-line display formatter for a data-model attribute value.
 *
 * This is deliberately **not** the editor-oriented `components/datamodel/*`
 * renderers. Those want `fieldPath`, `gridMode`, react-hook-form context,
 * the validations store, CRUD-view context, etc. — all irrelevant for
 * read-only surfaces (cards, list rows, badge chips).
 *
 * Section attributes on a `DataModelLayout` (see `PIM::SectionAttribute` in
 * `pim.rb`) serialize as stubs — just `{ name, ...params }`. The rich
 * metadata (`typeName`, `baseClass`, option-list keys) lives on the
 * top-level attribute registry. The formatter transparently enriches any
 * partial attribute via `dataModel.attribute(name)` so callers can pass the
 * section stub they already have without re-doing the lookup themselves.
 *
 * Scope: covers the attribute types the DAM asset card layout needs today
 * (`String`, `TextArea`, `Integer`, `Decimal`, `Boolean`, `Date`, `DateTime`,
 * and the `Enum` / `Codelist` / `EnumSet` / `CodelistSet` family — open
 * variants included). Everything else falls back to `String(value)` so a
 * mis-referenced attribute renders visibly rather than silently disappearing.
 */

const EMPTY_DISPLAY = '-';

const ENUM_TYPES: ReadonlySet<DataModelAttributeTypeName> = new Set([
    DataModelAttributeTypeName.Enum,
    DataModelAttributeTypeName.OpenEnum,
    DataModelAttributeTypeName.Codelist,
    DataModelAttributeTypeName.OpenCodelist,
]);

const ENUM_SET_TYPES: ReadonlySet<DataModelAttributeTypeName> = new Set([
    DataModelAttributeTypeName.EnumSet,
    DataModelAttributeTypeName.OpenEnumSet,
    DataModelAttributeTypeName.CodelistSet,
    DataModelAttributeTypeName.OpenCodelistSet,
]);

const isEmptyValue = (value: unknown): boolean => {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === 'string' && value.length === 0) {
        return true;
    }
    if (Array.isArray(value) && value.length === 0) {
        return true;
    }
    return false;
};

const resolveLocale = (): string => {
    const stored = localStorage.getItem(LOCAL_STORAGE.LANGUAGE_KEY);
    if (!stored) {
        return 'en';
    }
    return stored.replace('_', '-');
};

const formatDate = (value: unknown, locale: string, withTime: boolean): string => {
    const date = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(date.getTime())) {
        return EMPTY_DISPLAY;
    }
    return withTime
        ? date.toLocaleString(locale)
        : date.toLocaleDateString(locale);
};

const formatNumber = (value: unknown, locale: string): string => {
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(num)) {
        return String(value);
    }
    return num.toLocaleString(locale);
};

const useFormatAttributeValue = () => {

    const { translateOption } = useOptionTranslator();
    const { dataModel } = useDataModel();

    const formatValue = useCallback((attribute: DataModelAttribute, value: unknown): string => {

        if (isEmptyValue(value)) {
            return EMPTY_DISPLAY;
        }

        // Section-attribute stubs carry only `{ name, ...params }` — the rich
        // definition lives in the top-level attribute registry. Merge them so
        // downstream typeName / baseClass / option-list lookups all work.
        const registered = dataModel?.attribute(attribute.name) ?? undefined;
        const resolved: DataModelAttribute = registered
            ? { ...registered, ...attribute }
            : attribute;

        const locale = resolveLocale();
        const typeName = resolved.typeName;
        const baseClass = resolved.baseClass;

        if (typeName && ENUM_TYPES.has(typeName)) {
            return translateOption(String(value), resolved);
        }

        if (typeName && ENUM_SET_TYPES.has(typeName)) {
            const values = Array.isArray(value) ? value : [value];
            return values
                .map((v) => translateOption(String(v), resolved))
                .filter((label) => label.length > 0)
                .join(', ');
        }

        // Numeric attributes come in via typeName (Decimal) or baseClass
        // (Integer / Float). Ints/floats do not have a dedicated typeName —
        // that's why we key off baseClass here.
        if (typeName === DataModelAttributeTypeName.Decimal
            || baseClass === DataModelAttributeBaseClass.Integer
            || baseClass === DataModelAttributeBaseClass.Float) {
            return formatNumber(value, locale);
        }

        switch (typeName) {
            case DataModelAttributeTypeName.String:
            case DataModelAttributeTypeName.TextArea:
            case DataModelAttributeTypeName.Password:
                return String(value);

            case DataModelAttributeTypeName.Boolean:
                return value ? 'true' : 'false';

            case DataModelAttributeTypeName.Date:
                return formatDate(value, locale, false);

            case DataModelAttributeTypeName.DateTime:
                return formatDate(value, locale, true);

            default:
                // Attribute lookup failed or the type isn't specifically
                // handled — coerce to string so the value is at least visible
                // rather than silently swallowed.
                return String(value);
        }

    }, [translateOption, dataModel]);

    return { formatValue };

};

export { useFormatAttributeValue };
